/**
 * ConversationList - List of conversations (left sidebar)
 * Shows users in organization with last message and unread count
 */
import React, { useState, useEffect } from 'react';
import { chatAPI } from '../../utils/chatAPI';
import './ConversationList.css';

const ConversationList = ({ conversations, selectedUser, onSelectUser, organizationId }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [showAllUsers, setShowAllUsers] = useState(false);
  
  useEffect(() => {
    // Fetch all users in organization
    fetchAllUsers();
  }, [organizationId]);
  
  const fetchAllUsers = async () => {
    try {
      const users = await chatAPI.getChatUsers(organizationId);
      setAllUsers(users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  
  return (
    <div className="conversation-list">
      {/* Toggle between conversations and all users */}
      <div className="conversation-tabs">
        <button
          className={`conversation-tab ${!showAllUsers ? 'active' : ''}`}
          onClick={() => setShowAllUsers(false)}
        >
          Conversations
          {conversations.length > 0 && (
            <span className="conversation-count">{conversations.length}</span>
          )}
        </button>
        <button
          className={`conversation-tab ${showAllUsers ? 'active' : ''}`}
          onClick={() => setShowAllUsers(true)}
        >
          All Users
          <span className="conversation-count">{allUsers.length}</span>
        </button>
      </div>
      
      {/* Conversation Items */}
      <div className="conversation-items">
        {showAllUsers ? (
          // All Users View
          allUsers.length === 0 ? (
            <div className="conversation-empty">
              <p>No users found</p>
            </div>
          ) : (
            allUsers.map((user) => (
              <div
                key={user.id}
                className={`conversation-item ${selectedUser?.id === user.id ? 'active' : ''}`}
                onClick={() => onSelectUser(user)}
              >
                <div className="conversation-avatar">
                  <div className="avatar-circle">
                    {getInitials(user.name)}
                  </div>
                  <span className={`user-status ${user.is_online ? 'online' : 'offline'}`} />
                </div>
                
                <div className="conversation-content">
                  <div className="conversation-header">
                    <h4 className="conversation-name">{user.name}</h4>
                    <span className={`user-role role-${user.role.toLowerCase()}`}>
                      {user.role}
                    </span>
                  </div>
                  <p className="conversation-email">{user.email}</p>
                </div>
              </div>
            ))
          )
        ) : (
          // Conversations View
          conversations.length === 0 ? (
            <div className="conversation-empty">
              <p>No conversations yet</p>
              <button
                className="start-chat-btn"
                onClick={() => setShowAllUsers(true)}
              >
                Start a conversation
              </button>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.user_id}
                className={`conversation-item ${selectedUser?.id === conv.user_id ? 'active' : ''}`}
                onClick={() => onSelectUser({
                  id: conv.user_id,
                  name: conv.user_name,
                  email: conv.user_email,
                  role: conv.user_role,
                  is_online: conv.is_online,
                })}
              >
                <div className="conversation-avatar">
                  <div className="avatar-circle">
                    {getInitials(conv.user_name)}
                  </div>
                  <span className={`user-status ${conv.is_online ? 'online' : 'offline'}`} />
                </div>
                
                <div className="conversation-content">
                  <div className="conversation-header">
                    <h4 className="conversation-name">{conv.user_name}</h4>
                    <span className="conversation-time">
                      {formatTimestamp(conv.last_message_timestamp)}
                    </span>
                  </div>
                  
                  <div className="conversation-preview">
                    <p className="conversation-message">
                      {conv.last_message.length > 50
                        ? conv.last_message.substring(0, 50) + '...'
                        : conv.last_message}
                    </p>
                    {conv.unread_count > 0 && (
                      <span className="unread-badge">{conv.unread_count}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )
        )}
      </div>
    </div>
  );
};

export default ConversationList;
